import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import Combobox from '../../ui/Combobox';
import IconButton from '../../ui/IconButton';
import { cx } from '../../ui/classNames';
import { editorCommandForKey } from './keyboardCommands';
import CategoryPill from './CategoryPill';

const MIN_EDITOR_WIDTH = 220;
const VIEWPORT_GUTTER = 8;
// A suggestion picked with the pointer commits and keeps the cell active.
const COMMIT_IN_PLACE = Object.freeze({ type: 'commit', rowDelta: 0, columnDelta: 0 });

// The editor's paper: the app's popover frame without a shadow, 6px around
// the field, above tooltips (the suggestion list opens just under it).
const EDITOR_CLASS_NAME = 'fixed z-[1500] box-border rounded-lg border border-solid border-ui-line bg-ui-surface p-1.5 '
    + 'text-ui-ink';
// The shared cell-editor field look: a 6px radius and the divider colour,
// an accent border and soft accent ring while focused.
const FIELD_CLASS_NAME = cx(
    '!rounded-md !border-hairline !pl-[13px] !pr-[6px] hover:!border-hairline has-[:focus]:!border-accent',
    'has-[:focus]:!shadow-[0_0_0_2px_rgb(var(--c-accent)/0.16)]',
);

// Below the cell, at least as wide as the cell, and kept on screen on phones.
function editorPosition(anchorEl, anchorRect) {
    const rect = anchorEl?.isConnected ? anchorEl.getBoundingClientRect() : anchorRect;
    const width = Math.max(MIN_EDITOR_WIDTH, rect?.width || 0);
    const viewportWidth = typeof window === 'undefined' ? Infinity : window.innerWidth;
    return {
        top: rect?.bottom || 0,
        left: Math.max(0, Math.min(rect?.left || 0, viewportWidth - width - VIEWPORT_GUTTER)),
        width,
    };
}

const renderCategoryOption = (option) => <CategoryPill value={option} />;

/**
 * The cell editor popover: free text with the column's existing categories
 * as suggestions, shown as pills. Typing into a selected cell opens it in
 * replace mode with that character as the input. Enter / Shift+Enter / Tab /
 * Shift+Tab commit and move, Mod+Enter writes the value into the whole
 * selection, Escape (or the field's × button) cancels and leaving the field
 * commits in place. A suggestion highlighted with the arrow keys is what
 * those keys commit; one clicked commits at once.
 *
 * Mount one per edit (keyed by the owner) so every edit starts fresh.
 */
const SpreadsheetCellEditor = React.memo(function SpreadsheetCellEditor({
    anchorEl,
    anchorRect = null,
    label,
    inputLabel,
    options = [],
    value = '',
    initialInput = null,
    isMac = false,
    onCommit,
    onCancel,
}) {
    const replaceMode = initialInput != null;
    const [inputValue, setInputValue] = useState(replaceMode ? initialInput : value);
    // A suggestion reached with the arrow keys is what Enter commits.
    const [keyboardOption, setKeyboardOption] = useState(null);
    const finishedRef = useRef(false);
    // In edit mode the first click into the field selects its text.
    const selectOnClickRef = useRef(!replaceMode);

    const commit = (nextValue, command) => {
        if (finishedRef.current) return;
        finishedRef.current = true;
        onCommit(nextValue, command);
    };
    const cancel = () => {
        if (finishedRef.current) return;
        finishedRef.current = true;
        onCancel();
    };

    const position = editorPosition(anchorEl, anchorRect);
    return createPortal(
        <div
            role="dialog"
            aria-label={`Edit ${label}`}
            className={EDITOR_CLASS_NAME}
            style={{ top: position.top, left: position.left, width: position.width }}
        >
            <Combobox
                autoFocus
                aria-label={inputLabel || `Edit ${label}`}
                inputValue={inputValue}
                onInputChange={(nextValue) => {
                    setInputValue(nextValue);
                    setKeyboardOption(null);
                }}
                options={options}
                filterOnOpen={replaceMode}
                renderOption={renderCategoryOption}
                onHighlightChange={(option) => setKeyboardOption(option == null ? null : String(option))}
                onCommit={(option) => {
                    setInputValue(option);
                    commit(option, COMMIT_IN_PLACE);
                }}
                onFocus={(event) => {
                    if (!replaceMode) return;
                    // Keep typing after the character that opened the editor.
                    const end = event.target.value.length;
                    event.target.setSelectionRange?.(end, end);
                }}
                onClick={(event) => {
                    if (!selectOnClickRef.current) return;
                    selectOnClickRef.current = false;
                    const input = event.currentTarget;
                    if (input.selectionStart === input.selectionEnd) input.select();
                }}
                onBlur={() => commit(inputValue, null)}
                onKeyDown={(event) => {
                    const command = editorCommandForKey(event, { isMac });
                    if (!command) return;
                    event.preventDefault();
                    // The editor owns these keys; nothing around it may act on them too.
                    event.stopPropagation();
                    if (command.type === 'cancel') {
                        cancel();
                        return;
                    }
                    commit(keyboardOption ?? inputValue, command);
                }}
                className={FIELD_CLASS_NAME}
                endAdornment={inputValue ? (
                    <IconButton
                        label="Clear"
                        title="Clear"
                        tabIndex={-1}
                        // Keep focus in the field until the click cancels the edit.
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={cancel}
                    >
                        <X aria-hidden="true" className="size-5 [[data-compact]_&]:size-[17px]" />
                    </IconButton>
                ) : null}
            />
        </div>,
        document.body,
    );
});

export default SpreadsheetCellEditor;
